<template>
  <div class="lil">
    <!-- Empty state slot or default -->
    <div v-if="items.length === 0" class="lil__empty">
      <slot name="empty">
        <i v-if="emptyIcon" :class="`tim-icons ${emptyIcon}`" class="lil__empty-icon"></i>
        <p class="lil__empty-title">{{ emptyTitle || 'No items linked yet' }}</p>
        <p v-if="emptyHint" class="lil__empty-hint">{{ emptyHint }}</p>
      </slot>
    </div>

    <!-- Items -->
    <ul v-else class="lil__list">
      <li
        v-for="(item, idx) in items"
        :key="`lil-${item.id}`"
        class="lil__item"
      >
        <span class="lil__num">{{ idx + 1 }}</span>

        <div class="lil__body">
          <div class="lil__name">{{ item.name }}</div>
          <div v-if="itemMeta(item)" class="lil__meta">{{ itemMeta(item) }}</div>
        </div>

        <div class="lil__actions">
          <label v-if="supportRequired" class="lil__req">
            <el-switch
              :value="item.required"
              @change="onToggle(idx, 'required', $event)"
              :active-value="1"
              :inactive-value="0"
              active-color="#e14eca"
            />
            <span>Required</span>
          </label>

          <el-switch
            :value="item.active"
            @change="onToggle(idx, 'active', $event)"
            :active-value="1"
            :inactive-value="0"
            active-color="#e14eca"
          />

          <base-button link type="primary" size="sm" @click="$emit('remove', idx)" aria-label="Remove">
            <i class="tim-icons icon-simple-remove"></i>
          </base-button>
        </div>
      </li>
    </ul>

    <!--
      Add-from-catalog picker. NOTE: not wrapped in <base-input> — the
      base-input chrome (form-group margins, full-width container) forces
      the el-select popper to render at the wrapper's width instead of the
      select's. Matches InternalBookingFields' picker layout.
    -->
    <div v-if="addable.length > 0" class="lil__add-row">
      <el-select
        :value="null"
        :placeholder="addPlaceholder"
        class="select-primary lil__add-select"
        size="small"
        :popper-append-to-body="false"
        @change="onAdd"
      >
        <el-option
          v-for="opt in addable"
          :key="opt.id"
          :label="opt.name"
          :value="opt.id"
          class="select-primary"
        />
      </el-select>
    </div>
  </div>
</template>

<script>
import { Switch, Select, Option } from 'element-ui';

/**
 * LinkedItemList
 * Generic list of linked items (custom fields, payment methods, etc.) with
 * per-item active toggle, optional required toggle, remove button, and an
 * add-from-catalog dropdown at the bottom.
 *
 * Used in any surface that maintains a many-to-many link table between a
 * catalog and a parent entity (e.g., payment_page_payment_method_link,
 * custom_field_payment_page_link).
 *
 * @prop {Array}    items            Currently linked items. Each: { id, name, active, required?, ...meta }
 * @prop {Array}    addable          Items in the catalog NOT yet linked. Each: { id, name, ... }
 * @prop {Boolean}  supportRequired  Show the per-item Required toggle
 * @prop {Function} itemMeta         (item) => String meta line under the name
 * @prop {String}   addPlaceholder
 * @prop {String}   emptyIcon        tim-icons class
 * @prop {String}   emptyTitle
 * @prop {String}   emptyHint
 *
 * @event remove(idx)
 * @event update(idx, field, value)
 * @event add(catalogId)
 */
export default {
  name: 'LinkedItemList',
  components: {
    [Switch.name]: Switch,
    [Select.name]: Select,
    [Option.name]: Option,
  },
  props: {
    items: { type: Array, required: true },
    addable: { type: Array, default: () => [] },
    supportRequired: { type: Boolean, default: false },
    itemMeta: { type: Function, default: () => '' },
    addPlaceholder: { type: String, default: 'Add item from catalog' },
    emptyIcon: { type: String, default: '' },
    emptyTitle: { type: String, default: '' },
    emptyHint: { type: String, default: '' },
  },
  methods: {
    onToggle(idx, field, value) {
      this.$emit('update', idx, field, value);
    },
    onAdd(id) {
      if (id) this.$emit('add', id);
    },
  },
};
</script>

<style lang="scss" scoped>
.lil {
  &__empty {
    text-align: center;
    padding: 24px 16px;
    background: rgba(125, 125, 125, 0.04);
    border: 1px dashed rgba(125, 125, 125, 0.3);
    border-radius: 6px;
  }

  &__empty-icon {
    font-size: 28px;
    color: rgba(255, 255, 255, 0.45);
    display: inline-block;
    margin-bottom: 6px;
  }

  &__empty-title {
    color: rgba(255, 255, 255, 0.95);
    margin: 4px 0 2px;
    font-size: 13.5px;
  }

  &__empty-hint {
    color: rgba(255, 255, 255, 0.6);
    margin: 0;
    font-size: 11.5px;
    max-width: 44ch;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.5;
  }

  &__list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    background: rgba(125, 125, 125, 0.04);
    border: 1px solid rgba(125, 125, 125, 0.15);
    border-radius: 6px;
    margin-bottom: 6px;
    transition: border-color 0.12s ease;

    &:hover {
      border-color: rgba(125, 125, 125, 0.25);
    }
  }

  &__num {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.4);
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.04em;
    min-width: 16px;
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__name {
    color: rgba(255, 255, 255, 0.95);
    font-size: 13px;
    font-weight: 500;
  }

  &__meta {
    color: rgba(255, 255, 255, 0.55);
    font-size: 11px;
    margin-top: 2px;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__req {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    padding-right: 12px;
    border-right: 1px solid rgba(125, 125, 125, 0.18);
    color: rgba(255, 255, 255, 0.7);
    font-size: 11px;
    font-weight: normal;
    cursor: pointer;
  }

  &__add-row {
    margin-top: 10px;
    max-width: 320px;
  }
}
</style>

<style>
.white-content .lil__empty {
  background: rgba(125, 125, 125, 0.04);
}
.white-content .lil__empty-icon { color: rgba(0, 0, 0, 0.45); }
.white-content .lil__empty-title { color: rgba(0, 0, 0, 0.85); }
.white-content .lil__empty-hint { color: rgba(0, 0, 0, 0.6); }
.white-content .lil__item {
  background: rgba(125, 125, 125, 0.04);
  border-color: rgba(0, 0, 0, 0.08);
}
.white-content .lil__item:hover { border-color: rgba(0, 0, 0, 0.15); }
.white-content .lil__num { color: rgba(0, 0, 0, 0.55); }
.white-content .lil__name { color: rgba(0, 0, 0, 0.9); }
.white-content .lil__meta { color: rgba(0, 0, 0, 0.55); }
.white-content .lil__req {
  color: rgba(0, 0, 0, 0.7);
  border-right-color: rgba(0, 0, 0, 0.12);
}
</style>
